import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CircleAlert, Inbox } from 'lucide-react';
import { Card, Skeleton, cn } from '@eombor/ui';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';

export type CardTone = 'accent' | 'success' | 'warn' | 'info';

const CHIP: Record<CardTone, string> = {
  accent: 'bg-accent-soft text-accent',
  success: 'bg-success-soft text-success',
  warn: 'bg-warn-soft text-warn',
  info: 'bg-info-soft text-info',
};

/**
 * Blok sarlavhasi — belgicha, nom va uning ostidagi bitta izoh qatori.
 *
 * `Card` da alohida `subtitle` yo'q va u ATAYLAB qo'shilmadi: izoh faqat
 * shu bo'limga kerak, umumiy komponentga esa har ekran o'z maydonini
 * qo'shaversa, `Card` "hamma narsa" bo'lib ketardi.
 */
const ChartTitle = ({
  icon: Icon,
  tone,
  title,
  hint,
}: {
  icon: LucideIcon;
  tone: CardTone;
  title: string;
  hint: string;
}) => (
  <div className="flex min-w-0 items-center gap-3">
    <span
      aria-hidden
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
        CHIP[tone],
      )}
    >
      <Icon size={17} />
    </span>
    <div className="flex min-w-0 flex-col gap-0.5">
      <h3 className="truncate text-base font-semibold tracking-tight text-text">{title}</h3>
      <p className="truncate text-xs text-text-faint">{hint}</p>
    </div>
  </div>
);

export interface ReportCardProps {
  icon: LucideIcon;
  tone: CardTone;
  title: string;
  hint: string;
  pending: boolean;
  error: ApiClientError | null;
  /** `true` — so'rov muvaffaqiyatli, lekin ko'rsatadigan narsa yo'q. */
  empty: boolean;
  emptyText: string;
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Har bir hisobot blokining TASHQI qobig'i.
 *
 * To'rt holat bir joyda: yuklanmoqda, xato, bo'sh, ma'lumot. Har blok buni
 * o'zi yozsa, ular asta-sekin bir-biridan farq qila boshlardi — bittasi
 * xatoni ko'rsatmay, bo'shini "0" deb chizib qo'yardi.
 *
 * Xato blokni O'CHIRMAYDI, ichida ko'rinadi: qolgan uchta blok baribir
 * ishlaydi va butun ekranni yo'qotishning ma'nosi yo'q.
 */
export const ReportCard = ({
  icon,
  tone,
  title,
  hint,
  pending,
  error,
  empty,
  emptyText,
  actions,
  children,
}: ReportCardProps) => (
  <Card title={<ChartTitle icon={icon} tone={tone} title={title} hint={hint} />} actions={actions}>
    {pending ? (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label={t('report.loading')}>
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-32 w-full" />
      </div>
    ) : error !== null ? (
      <div className="flex flex-col items-center gap-2 rounded-[var(--radius-md)] border border-danger/25 bg-danger-soft px-6 py-8 text-center">
        <CircleAlert size={22} className="text-danger" aria-hidden />
        <p className="text-sm font-medium text-danger">{t('report.failed')}</p>
        <p className="text-sm text-text-muted">{error.message}</p>
      </div>
    ) : empty ? (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
        <Inbox size={22} className="text-text-faint" aria-hidden />
        <p className="text-sm text-text-faint">{emptyText}</p>
      </div>
    ) : (
      children
    )}
  </Card>
);
